/**
 * Módulo de Gerenciamento da Instância Uazapi (Conexões/Configurações)
 * Arquitetura: Single Responsibility -> Só gerencia o lifecycle da instância.
 * Segurança: Oculta endpoint direto, depende do proxy para chamadas sensíveis.
 */

import * as uazapi from './index';
import { supabase } from '../../lib/supabase';
import { setupWebhook } from './webhook';
import { reconnectAndGetQR } from './reconnect';
import { preferPermanentAvatarUrl } from '../../utils/failedImageCache';

export const createInstance = async (instanceName: string, phoneNumber?: string) => {
    const connectRes = await uazapi.connectInstance(instanceName);
    // Configura o webhook automaticamente na criação
    await setupWebhook(instanceName);
    return connectRes;
};

export const getQRCode = async (instanceName: string) => {
    const qr = await uazapi.getQrCode(instanceName);
    return {
        base64: qr?.base64 || qr?.code,
        code: qr?.code
    };
};

function isUazapiSessionConnected(data: any): boolean {
    const state = String(
        data?.state ?? data?.instance?.state ?? data?.instance?.status ?? '',
    ).toLowerCase();
    return (
        data?.status?.connected === true ||
        data?.connected === true ||
        state === 'connected' ||
        state === 'open'
    );
}

export const getConnectionState = async (instanceName: string) => {
    const data = await uazapi.getInstanceStatus(instanceName);
    let normalizedState = 'close';

    const isQRCode =
        data?.state?.toLowerCase() === 'qrcode' ||
        data?.instance?.status === 'connecting' ||
        !!data?.qrcode || !!data?.base64;

    if (isUazapiSessionConnected(data)) {
        normalizedState = 'open';
    } else if (isQRCode) {
        normalizedState = 'connecting';
    }

    console.log(`[getConnectionState] ${instanceName}: normalizedState=${normalizedState}`, 
        JSON.stringify(data).substring(0, 200));

    return {
        instance: {
            instanceName,
            state: normalizedState
        }
    };
};

export const fetchInstanceDetails = async (instanceName: string) => {
    try {
        const data = await uazapi.getInstanceStatus(instanceName);
        if (!data) return null;

        const owner = data?.instance?.owner || data?.owner || data?.status?.jid?.user || null;

        return {
            name: data?.instance?.profileName || data?.instance?.name || instanceName,
            number: owner,
            profilePictureUrl: data?.instance?.profilePicUrl || data?.profilePicUrl || null,
            connectionStatus: isUazapiSessionConnected(data) ? 'open' : 'close'
        };
    } catch {
        return null;
    }
};

export const getInstanceProfile = async (instanceName: string) => {
    try {
        const data = await uazapi.getInstanceStatus(instanceName);
        const owner = data?.instance?.owner || data?.owner || data?.status?.jid?.user || null;
        return {
            name: data?.instance?.profileName || data?.instance?.name || instanceName,
            number: owner,
            profilePictureUrl: data?.instance?.profilePicUrl || data?.profilePicUrl || null
        };
    } catch {
        return { name: instanceName, number: null, profilePictureUrl: null };
    }
};

export const deleteInstance = async (instanceName: string) => {
    return await uazapi.removeInstance(instanceName);
};

export const disconnectInstance = async (instanceName: string) => {
    try {
        await uazapi.logoutInstance(instanceName);
        const now = new Date().toISOString();
        await supabase
            .from('whatsapp_instances')
            .update({ status: 'disconnected', updated_at: now })
            .eq('instance_name', instanceName);
        // UI lê integrations.status — manter as duas tabelas sincronizadas
        await supabase
            .from('integrations')
            .update({ status: 'disconnected', updated_at: now })
            .eq('type', 'whatsapp')
            .eq('config->>instance_name', instanceName);
        return true;
    } catch {
        return false;
    }
};

export const reconnectInstance = async (instanceName: string) => {
    try {
        const result = await reconnectAndGetQR(instanceName);
        const now = new Date().toISOString();

        if (result.connected) {
            const profile = await getInstanceProfile(instanceName);
            const phone = profile?.number
                ? String(profile.number).replace('@s.whatsapp.net', '').replace(/\D/g, '')
                : '';

            const { data: existingInstance } = await supabase
                .from('whatsapp_instances')
                .select('profile_picture_url')
                .eq('instance_name', instanceName)
                .maybeSingle();
            const pictureUrl = preferPermanentAvatarUrl(
                existingInstance?.profile_picture_url,
                profile?.profilePictureUrl,
            );

            await supabase.from('whatsapp_instances')
                .update({
                    status: 'connected',
                    updated_at: now,
                    last_connected_at: now,
                    ...(phone ? { phone_number: phone } : {}),
                    ...(pictureUrl ? { profile_picture_url: pictureUrl } : {}),
                })
                .eq('instance_name', instanceName);

            const { data: integ } = await supabase
                .from('integrations')
                .select('id, config')
                .eq('type', 'whatsapp')
                .eq('config->>instance_name', instanceName)
                .maybeSingle();

            if (integ) {
                const keptPic = preferPermanentAvatarUrl(
                    (integ.config as { profile_pic?: string } | null)?.profile_pic,
                    pictureUrl,
                );
                await supabase.from('integrations').update({
                    status: 'connected',
                    updated_at: now,
                    config: {
                        ...(integ.config || {}),
                        instance_name: instanceName,
                        ...(keptPic ? { profile_pic: keptPic } : {}),
                        ...(phone ? { display_number: phone, owner_jid: `${phone}@s.whatsapp.net` } : {}),
                    },
                }).eq('id', integ.id);
            }
            return { base64: null, code: null, alreadyConnected: true };
        }

        if (result.base64) {
            await supabase.from('whatsapp_instances')
                .update({ status: 'qr_pending', updated_at: now })
                .eq('instance_name', instanceName);
            return { base64: result.base64, code: result.code };
        }

        return null;
    } catch (err) {
        console.error('[Instance] Erro ao reconectar:', err);
        return null;
    }
};

export const deleteInstanceCompletely = async (instanceName: string, integrationId: string) => {
    try {
        try {
            await uazapi.removeInstance(instanceName);
        } catch (apiErr) {
            console.warn('[Instance] UazAPI delete falhou (seguindo limpeza local):', apiErr);
        }
        await supabase.from('whatsapp_instances').delete().eq('integration_id', integrationId);
        const { error } = await supabase.from('integrations').delete().eq('id', integrationId);
        if (error) throw error;
        return true;
    } catch (err) {
        console.error('[Instance] Erro ao excluir completamente:', err);
        return false;
    }
};

export const getInstanceStatusFromDB = async (instanceName: string) => {
    const { data } = await supabase.from('whatsapp_instances').select('*').eq('instance_name', instanceName).single();
    if (!data) return null;
    return {
        instanceName: data.instance_name,
        status: data.status,
        phoneNumber: data.phone_number
    };
};

export const getOrganizationInstances = async (organizationId: string) => {
    const { data } = await supabase.from('whatsapp_instances').select('*').eq('organization_id', organizationId);
    return data || [];
};
